import { call, getContext, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { httpSuccess } from 'app/services/serviceHelpers';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { selectUser } from 'oidc/user.selectors';
import { ICandidateEventSubmissionControl } from 'app/models/Candidate/CandidateEventSubmissionControl';
import { candidateEventSubmissionControlActions } from './candidateEventSubmissionControl.redux';
import { PlacementService } from 'app/services/PlacementServices/placement-service';

const sagaArea =
  'src/app/components/Candidate/CandidateProfile/SubmissionPaused/store/candidateEventSubmissionControl.saga.ts';

function* fetchSubmissionControlsListSaga(
  action: ReturnType<typeof candidateEventSubmissionControlActions.fetchSubmissionControlsList>,
) {
  const placementService: PlacementService = yield getContext('placementService');
  const { travelerId } = action.payload;

  try {
    const response = yield call(placementService.getCandidateEventSubmissionControls, travelerId);
    if (httpSuccess(response?.status)) {
      yield put(
        candidateEventSubmissionControlActions.fetchSubmissionControlsListSuccess({
          travelerId,
          items: response.data ?? [],
        }),
      );
    } else {
      yield put(candidateEventSubmissionControlActions.fetchSubmissionControlsListFailure({ travelerId }));
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'fetchSubmissionControlsListSaga',
        area: sagaArea,
      },
    });
    yield put(candidateEventSubmissionControlActions.fetchSubmissionControlsListFailure({ travelerId }));
  }
}

function* fetchSubmissionControlByPlacementSaga(
  action: ReturnType<typeof candidateEventSubmissionControlActions.fetchSubmissionControlByPlacement>,
) {
  const placementService: PlacementService = yield getContext('placementService');
  const { placementId } = action.payload;

  try {
    const response = yield call(placementService.getCandidateEventSubmissionControlByPlacementId, placementId);
    if (httpSuccess(response?.status)) {
      const control: ICandidateEventSubmissionControl | null =
        response.data?.affiliationId > 0 ? response.data : null;
      yield put(
        candidateEventSubmissionControlActions.fetchSubmissionControlByPlacementSuccess({
          placementId,
          control,
        }),
      );
    } else {
      yield put(candidateEventSubmissionControlActions.fetchSubmissionControlByPlacementFailure({ placementId }));
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'fetchSubmissionControlByPlacementSaga',
        area: sagaArea,
      },
    });
    yield put(candidateEventSubmissionControlActions.fetchSubmissionControlByPlacementFailure({ placementId }));
  }
}

function* updateSubmissionPauseSaga(action: ReturnType<typeof candidateEventSubmissionControlActions.updateSubmissionPause>) {
  const placementService: PlacementService = yield getContext('placementService');
  const user = yield select(selectUser);
  const { travelerId, affiliationId, isSubmissionPaused } = action.payload;

  try {
    const response = yield call(placementService.updateCandidateEventSubmissionPause, {
      travelerId,
      affiliationId,
      isSubmissionPaused,
      userId: user?.userInfo?.employeeId,
    });
    if (httpSuccess(response?.status)) {
      const updated: ICandidateEventSubmissionControl | null =
        response.data?.submissionControl ?? response.data ?? null;
      if (updated?.affiliationId > 0) {
        yield put(candidateEventSubmissionControlActions.updateSubmissionPauseSuccess(updated));
      } else {
        yield put(candidateEventSubmissionControlActions.updateSubmissionPauseFailure({ affiliationId }));
      }
    } else {
      yield put(candidateEventSubmissionControlActions.updateSubmissionPauseFailure({ affiliationId }));
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'updateSubmissionPauseSaga',
        area: sagaArea,
      },
    });
    yield put(candidateEventSubmissionControlActions.updateSubmissionPauseFailure({ affiliationId }));
  }
}

export function* candidateEventSubmissionControlSaga() {
  yield takeLatest(candidateEventSubmissionControlActions.fetchSubmissionControlsList.type, fetchSubmissionControlsListSaga);
  yield takeLatest(candidateEventSubmissionControlActions.fetchSubmissionControlByPlacement.type, fetchSubmissionControlByPlacementSaga);
  yield takeEvery(candidateEventSubmissionControlActions.updateSubmissionPause.type, updateSubmissionPauseSaga);
}
